import { AsyncMqttClient } from 'async-mqtt';
import dotenv from 'dotenv';
import fs from 'fs';
import { isEmpty, isNil, pick } from 'lodash';
import { Context, ServiceBroker } from 'moleculer';

import Communication from './common/communication';
import Configs, { IBaseConfig, IConfig, IRuntimeConfigs } from './common/configs';
import Logger from './common/logger';
import Services, { IMoleculerConfigs } from './common/services';
import {
  checkStartRequirements, executeMigrations, getAliveDestinations, IMigration
} from './common/utils';

dotenv.config();

const singleton = Symbol('singleton');
const singletonEnforcer = Symbol('singletonEnforcer');

type IStatus = 'started' | 'stopped';
type IFeedback = 'ok' | 'nok';

export type ICallback<T = void> = (params?: ICallbackParams) => Promise<T> | T;
export type ICallbackParams = { topic: string } | { ctx: Context } | undefined;
export type IConfigsCallback = (message: IConfigsMessage) => Promise<void>;
export interface IFbxSdk extends IBaseConfig {
  onStart?: ICallback;
  onStop?: ICallback;
  onShutdown?: ICallback;
  onConfigs?: IConfigsCallback;
  onAlive?: ICallback<Record<string, unknown>>;
  overrides?: {
    configs?: Configs;
    logger?: Logger;
    communication?: Communication;
    services?: Services;
  };
  moleculer?: IMoleculerConfigs;
  autoStart?: boolean;
  migrations?: IMigration[];
}

interface IConfigsMessage {
  path: string;
  configs: Record<string, unknown>;
}

interface IShutdownConfigs {
  code: number;
}

export default class FbxSdk {
  public static init(params?: IFbxSdk): Promise<void> {
    return FbxSdk.instance.init(params);
  }

  public static start(message?: IRuntimeConfigs, params?: ICallbackParams): Promise<void> {
    return FbxSdk.instance.start(message, params);
  }

  public static stop(): Promise<void> {
    return FbxSdk.instance.stop();
  }

  public static shutdown(message?: IShutdownConfigs, params?: ICallbackParams): Promise<void> {
    return FbxSdk.instance.shutdown(message, params);
  }

  public static setup(): Promise<void> {
    return FbxSdk.instance.setup();
  }

  public static setOnStart(callback: ICallback): void {
    FbxSdk.instance.setOnStart(callback);
  }

  public static setOnStop(callback: ICallback): void {
    FbxSdk.instance.setOnStop(callback);
  }

  public static setOnShutdown(callback: ICallback): void {
    FbxSdk.instance.setOnShutdown(callback);
  }

  public static setOnAlive(callback: ICallback<Record<string, unknown>>): void {
    FbxSdk.instance.setOnAlive(callback);
  }

  private onStart: ICallback;

  private onStop: ICallback;

  private onShutdown: ICallback;

  private onConfigs: IConfigsCallback;

  private onAlive: ICallback<Record<string, unknown>>;

  private aliveTimeout: ReturnType<typeof setTimeout>;;

  private status: IStatus = 'stopped';

  private autoStart = false;

  private moleculerConfigs: IMoleculerConfigs;

  private migrations: IMigration[];

  constructor(enforcer: symbol) {
    if (enforcer !== singletonEnforcer) { throw new Error('Cannot construct singleton'); }
  }

  public init = async (params: IFbxSdk = {}): Promise<void> => {
    const { onStart, onStop, onShutdown, onAlive, onConfigs, overrides = {}, autoStart = false, moleculer, migrations = [] } = params;

    const defaultCb = async () => { return Promise.resolve(); };
    const defaultAlive = () => { return {}; };
    this.onStart = onStart || defaultCb;
    this.onStop = onStop || defaultCb;
    this.onShutdown = onShutdown || defaultCb;
    this.onConfigs = onConfigs || this.defaultConfigsCb;
    this.onAlive = onAlive || defaultAlive;
    this.autoStart = autoStart;

    this.moleculerConfigs = moleculer;
    this.migrations = migrations;

    // set overrides
    const { configs, logger, communication, services } = overrides;
    if (configs) { Configs.instance = configs; }
    if (logger) { Logger.instance = logger; }
    if (communication) { Communication.instance = communication; }
    if (services) { Services.instance = services; }

    // merge configs
    const allowedKeys = Object.keys(Configs.configs);
    const allowedConfigs = pick(params, allowedKeys);
    Configs.merge(allowedConfigs);

    await Logger.init(Configs.configs);

    process.on('SIGTERM', () => {
      Logger.debug('Caught SIGTERM');
      this.shutdown({ code: 0 });
    });
    process.on('SIGINT', () => {
      Logger.debug(`Caught SIGINT ${process.pid}`);
      this.shutdown({ code: 0 });
    });
    process.on('uncaughtException', (error) => {
      Logger.error('Uncaught exception', error.stack);
      this.shutdown({ code: 1 });
    });
    process.on('exit', () => {
      // console.log('exiting core');
    });
  }

  public setup = async (): Promise<void> => {
    // Execute migrations
    executeMigrations(this.migrations);

    // connect to broker
    await Communication.connect();
    // default subscriptions and listeners
    Communication.addListener('logger', 'logger', Logger.instance.defaultListener);
    Communication.addListener('start', 'start', (topic: string, message) => this.start(message, { topic }));
    Communication.addListener('stop', 'stop', (topic: string) => this.stop({ topic }));
    Communication.addListener('restart', 'restart', (topic: string, message) => this.restart(message, { topic }));
    Communication.addListener('configs', 'configs', (topic: string, message) => this.onConfigs(message));
    Communication.addListener('shutdown', 'shutdown', (topic: string, message) => this.shutdown(message, { topic }));
    await Communication.subscribe('#');

    if (this.moleculerConfigs?.enabled) {
      // init default service
      Services.init({
        ...this.moleculerConfigs,
        schema: {
          ...this.moleculerConfigs?.schema,
          actions: {
            ...this.moleculerConfigs?.schema?.actions,
            start: (ctx: Context<IRuntimeConfigs>) => this.start(ctx.params, { ctx }),
            stop: (ctx: Context) => this.stop({ ctx }),
            restart: (ctx: Context<IRuntimeConfigs>) => this.restart(ctx.params, { ctx }),
            configs: (ctx: Context<IConfigsMessage>) => this.onConfigs(ctx.params),
            shutdown: (ctx: Context<IShutdownConfigs>) => this.shutdown(ctx.params, { ctx }),
            alive: () => this.alive(),
          }
        }
      });
      await Services.start();
    }

    // send ready to core
    await Communication.systemPublish('ready');

    // auto start mode
    if (this.autoStart === true) {
      await this.start();
    }
  }

  public start = async (message?: IRuntimeConfigs, params?: ICallbackParams): Promise<void> => {
    try {
      Logger.debug('starting...');
      // merge runtime configs
      if (!isNil(message)) {
        Logger.debug('Received runtime configs', message);
        Configs.set('runTime', message);
      }
      // start sending alive
      this.alive();

      // Check start requirements
      const reasons = checkStartRequirements();
      if (!isEmpty(reasons)) {
        Logger.error('Module cannot start. Requirements are not satisfied.');
        reasons.forEach(r => Logger.error(r));
        return;
      }

      Logger.info('Start requirements satisfied.');

      // start module business logic
      await this.onStart(params);
      this.status = 'started';

      // Send post-start event
      await Services.broadcast('post-start');
    } catch (error) {
      Logger.error(`unable to start ${error}`);
    }
  }

  public stop = async (params?: ICallbackParams): Promise<void> => {
    try {
      Logger.debug('stopping...');
      clearTimeout(this.aliveTimeout);

      // stop module business logic
      await this.onStop(params);
      this.status = 'stopped';

      // Send post-stop event
      await Services.broadcast('post-stop');
    } catch (error) {
      Logger.error(`unable to stop ${error}`);
    }
  }

  public restart = async (message?: IRuntimeConfigs, params?: ICallbackParams): Promise<void> => {
    await this.stop(params);
    await this.start(message, params);
  }

  public shutdown = async (message?: IShutdownConfigs, params?: ICallbackParams): Promise<void> => {
    const { code = 0 } = message || {};
    Logger.info(`shutdown with code ${code}`);
    let status: IFeedback = 'ok';
    try {
      await this.stop();
      await this.onShutdown(params);
    } catch (e) {
      status = 'nok';
      Logger.info(`unable to shutdown ${e}`);
    } finally {
      await Communication.systemPublish('shutdown', { status });
      await Communication.disconnect();
    }
    Logger.info('shutdown completed, exit');
    process.exit(code);
  }

  public alive = (): void => {
    const { aliveTimeout } = Configs.configs.runTime;
    clearTimeout(this.aliveTimeout);
    this.aliveTimeout = setTimeout(() => {
      const alivePayload = {
        lastSeen: new Date().toISOString(),
        status: this.status,
        data: this.onAlive(),
      };
      Communication.systemPublish('alive', alivePayload);
      Services.broadcast('alive', {
        node: Services.instance.getLocalNodeInfo(),
        payload: alivePayload,
      }, getAliveDestinations());
      this.alive();
    }, aliveTimeout);
  }

  public setOnStart = (callback: ICallback): void => {
    this.onStart = callback;
  }

  public setOnStop = (callback: ICallback): void => {
    this.onStop = callback;
  }

  public setOnShutdown = (callback: ICallback): void => {
    this.onShutdown = callback;
  }

  public setOnAlive = (callback: ICallback<Record<string, unknown>>): void => {
    this.onAlive = callback;
  }

  private defaultConfigsCb = async (message: IConfigsMessage): Promise<void> => {
    const { path, configs = {} } = message;
    Configs.mergeFromFile(path);
    Configs.merge(configs);
    if (path && fs.existsSync(path)) {
      fs.unlinkSync(path);
    }
    Configs.instance.reload();
    return Promise.resolve();
  }

  static get instance(): FbxSdk {
    if (!this[singleton]) {
      this[singleton] = new FbxSdk(singletonEnforcer);
    }
    return this[singleton];
  }

  static get configs(): IConfig {
    return Configs.configs;
  }

  static get logger(): Logger {
    return Logger.instance;
  }

  static get communication(): AsyncMqttClient {
    return Communication.client;
  }

  static get broker(): ServiceBroker {
    return Services.broker;
  }

  public static destroy(): void {
    this[singleton] = undefined;
  }
}
